import { useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion, AnimatePresence } from "framer-motion";
import { Check, CheckCheck, Search, Mic, MicOff, Phone, PhoneOff, Video, Volume2, Play, CheckCircle2, Menu, ChevronLeft, Home as HomeIcon, Send, UserCircle } from "lucide-react";
import { useT } from "../i18n";
import D from "../content/demos";
import { Plate } from "./kit";
import { Mark } from "./Brand";

const ICON = "h-4 w-4 stroke-[1.75]";
const time = (i) => `08:${String(14 + i).padStart(2, "0")}`;

// Builds the ordered steps of each scenario from localized strings.
const build = (k, d) => {
  const msgs = (arr, from = 0) => arr.map(([who, text], i) => ({ kind: "msg", who, text, t: time(from + i), d: 1500 }));
  switch (k) {
    case "search": return [{ kind: "search", d: 1800 }, { kind: "result", d: 1300 }, ...msgs(d.search)];
    case "photo": return [{ kind: "photo", src: "/images/uc-blocked.webp", t: time(0), d: 1300 }, ...msgs(d.photo, 1)];
    case "lights": return [{ kind: "photo", src: "/images/uc-lights.webp", t: time(0), d: 1300 }, ...msgs(d.lights, 1)];
    case "coord": return msgs(d.coord);
    case "voice": return [{ kind: "voice", t: time(0), d: 3600 }, { kind: "msg", who: "them", text: d.voiceReply, t: time(1), d: 1500 }];
    case "scene": return [{ kind: "scene", d: 1200 }, ...msgs(d.scene), { kind: "done", d: 2000 }];
    default: return [{ kind: k, d: 99999 }];
  }
};

const Bubble = ({ s, d }) => (
  <div className={`flex ${s.who === "me" ? "justify-end" : "justify-start"}`}>
    <div className={`max-w-[82%] rounded-2xl px-3 py-2 text-[13px] leading-snug ${s.who === "me" ? "rounded-br-sm bg-[#2E3CFD] text-white" : "rounded-bl-sm bg-white text-[#171940]"}`}>
      <p>{s.text}</p>
      <p className={`mt-1 flex items-center justify-end gap-1 text-[10px] ${s.who === "me" ? "text-white/75" : "text-[#171940]/60"}`}>
        {s.t}{s.who === "me" && <CheckCheck className="h-3.5 w-3.5" aria-label={d.sent} />}
      </p>
    </div>
  </div>
);

const Typing = ({ text, run }) => {
  const [n, setN] = useState(run ? 0 : text.length);
  useEffect(() => {
    if (!run) { setN(text.length); return undefined; }
    const id = setInterval(() => setN((v) => (v >= text.length ? v : v + 1)), 110);
    return () => clearInterval(id);
  }, [run, text]);
  return <span className="font-plate font-semibold tracking-[0.08em]">{text.slice(0, n)}<span className="animate-pulse">|</span></span>;
};

const Voice = ({ d, run, s }) => {
  const [p, setP] = useState(run ? 0 : 1);
  useEffect(() => {
    if (!run) return undefined;
    const id = setInterval(() => setP((v) => Math.min(1, v + 0.04)), 120);
    return () => clearInterval(id);
  }, [run]);
  const bars = [6, 12, 9, 16, 11, 18, 8, 14, 10, 17, 7, 13, 9, 15, 6, 12];
  return (
    <div className="flex justify-end">
      <div className="flex w-[78%] items-center gap-2 rounded-2xl rounded-br-sm bg-[#2E3CFD] px-3 py-2.5 text-white" aria-label={d.voice}>
        <Play className="h-4 w-4 shrink-0 fill-white" aria-hidden />
        <div className="flex h-5 flex-1 items-center gap-[3px]">
          {bars.map((h, i) => <span key={i} className={`w-[3px] rounded-full transition-colors ${i / bars.length < p ? "bg-white" : "bg-white/40"}`} style={{ height: h }} />)}
        </div>
        <span className="font-mono text-[10px]">0:{String(Math.round(p * 8)).padStart(2, "0")}</span>
        <span className="flex items-center gap-1 text-[10px] text-white/75">{s.t}<CheckCheck className="h-3.5 w-3.5" aria-hidden /></span>
      </div>
    </div>
  );
};

const Controls = ({ d, video }) => (
  <div className="flex justify-center gap-3">
    {[[MicOff, d.mute], video ? [Video, d.camera] : [Volume2, d.speaker]].map(([I, l]) => (
      <span key={l} className="grid h-11 w-11 place-items-center rounded-full bg-white/15 text-white" title={l}><I className={ICON} aria-label={l} /></span>
    ))}
    <span className="grid h-11 w-11 place-items-center rounded-full bg-[#e5484d] text-white" title={d.end}><PhoneOff className={ICON} aria-label={d.end} /></span>
  </div>
);

const useTimer = (run, startAfter) => {
  const [sec, setSec] = useState(run ? -1 : 42);
  useEffect(() => {
    if (!run) { setSec(42); return undefined; }
    setSec(-1);
    const t = setTimeout(() => setSec(0), startAfter);
    const id = setInterval(() => setSec((v) => (v < 0 ? v : v + 1)), 1000);
    return () => { clearTimeout(t); clearInterval(id); };
  }, [run, startAfter]);
  return sec;
};

export const Call = ({ d, run, plate }) => {
  const sec = useTimer(run, 2400);
  return (
    <div className="flex h-full flex-col items-center justify-between bg-[#171940] px-5 py-8 text-white">
      <div className="text-center">
        <p className="text-xs uppercase tracking-[0.14em] text-white/70">{d.audioCall}</p>
        <div className="relative mx-auto mt-6 grid h-20 w-20 place-items-center rounded-full bg-[#2E3CFD]">
          {sec < 0 && run && <span className="absolute inset-0 animate-ping rounded-full bg-[#2E3CFD]/50" />}
          <Phone className="h-7 w-7" aria-hidden />
        </div>
        <div className="mt-5"><Plate text={plate} size="sm" /></div>
        <p className="mt-4 text-sm font-medium" aria-live="polite">{sec < 0 ? d.calling : <span className="inline-flex items-center gap-1.5 text-[#7ee2a8]"><CheckCircle2 className="h-4 w-4" aria-hidden />{d.connected} · 0:{String(sec % 60).padStart(2, "0")}</span>}</p>
      </div>
      <Controls d={d} />
    </div>
  );
};

const Person = ({ tone }) => (
  <svg viewBox="0 0 120 120" className="h-full w-full" aria-hidden><rect width="120" height="120" fill={tone} /><circle cx="60" cy="48" r="20" fill="#ffffff" opacity=".85" /><path d="M22 120c4-26 20-38 38-38s34 12 38 38z" fill="#ffffff" opacity=".85" /></svg>
);

const VideoCall = ({ d, run }) => {
  const sec = useTimer(run, 1200);
  return (
    <div className="relative flex h-full flex-col bg-[#0d0f2b] text-white">
      <div className="relative flex-1 overflow-hidden">
        <img src="/images/uc-blocked.webp" alt="" className="absolute inset-0 h-full w-full object-cover opacity-80" />
        <span className="absolute left-3 top-3 rounded-full bg-black/50 px-2.5 py-1 text-[11px]">{d.videoCall} · {sec < 0 ? d.calling : `0:${String(sec % 60).padStart(2, "0")}`}</span>
        <div className="absolute bottom-3 right-3 h-24 w-16 overflow-hidden rounded-xl border-2 border-white/80"><Person tone="#2E3CFD" /><span className="absolute bottom-1 left-1 text-[9px]">{d.you}</span></div>
      </div>
      <div className="py-4"><Controls d={d} video /></div>
    </div>
  );
};

const Scene = ({ moved, d }) => (
  <svg viewBox="0 0 300 150" className="w-full rounded-xl bg-[#e9ebf5] dark:bg-[#20234d]" role="img" aria-label={d.sceneDone}>
    <rect x="0" y="0" width="300" height="150" fill="none" />
    {[40, 110, 180, 250].map((x) => <line key={x} x1={x} y1="0" x2={x} y2="60" stroke="#9aa0c8" strokeWidth="2" strokeDasharray="4 4" />)}
    <rect x="58" y="10" width="34" height="46" rx="6" fill="#8a90b8" />
    <rect x="198" y="10" width="34" height="46" rx="6" fill="#8a90b8" />
    <rect x="128" y="10" width="34" height="46" rx="6" fill="#2E3CFD" />
    <motion.g animate={{ x: moved ? 100 : 0 }} transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}>
      <rect x="112" y="78" width="66" height="34" rx="7" fill="#F5C842" />
      <rect x="122" y="84" width="16" height="22" rx="3" fill="#171940" opacity=".35" />
    </motion.g>
    <text x="150" y="140" textAnchor="middle" fontSize="11" fill={moved ? "#1f9d61" : "#c2410c"} fontFamily="Inter, sans-serif">{moved ? `✓ ${d.sceneDone}` : "!"}</text>
  </svg>
);

const Step = ({ s, d, run, plate, isLast }) => {
  if (s.kind === "msg") return <Bubble s={s} d={d} />;
  if (s.kind === "voice") return <Voice d={d} run={run && isLast} s={s} />;
  if (s.kind === "photo") return (
    <div className="flex justify-end"><div className="w-[62%] overflow-hidden rounded-2xl rounded-br-sm bg-[#2E3CFD] p-1"><img src={s.src} alt="" className="aspect-[4/3] w-full rounded-xl object-cover" /><p className="flex items-center justify-end gap-1 px-1.5 py-1 text-[10px] text-white/75">{s.t}<CheckCheck className="h-3.5 w-3.5" aria-hidden /></p></div></div>
  );
  if (s.kind === "search") return (
    <div className="flex items-center justify-between rounded-xl border border-[#2E3CFD]/60 bg-[#0d1543] px-3 py-2.5 text-sm text-white"><Typing text={plate} run={run && isLast} /><Search className={`${ICON} text-[#F5C842]`} aria-hidden /></div>
  );
  if (s.kind === "result") return (
    <div className="rounded-xl bg-white px-3 py-2.5 text-[#171940]"><Plate text={plate} size="sm" /><div className="mt-2 flex items-center justify-between gap-2"><p className="flex min-w-0 items-center gap-1 text-[11px] text-[#171940]/70"><CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-[#1f9d61]" aria-hidden />{d.registered}</p><span className="shrink-0 rounded-full bg-[#2E3CFD] px-3 py-1 text-xs text-white">{d.open}</span></div></div>
  );
  return null;
};

// Steps a scenario forward while visible; shows the final state when paused or with reduced motion.
const useSteps = (steps, animate, key) => {
  const [n, setN] = useState(steps.length);
  useEffect(() => {
    if (!animate) { setN(steps.length); return undefined; }
    let i = 1;
    let t;
    setN(1);
    const next = () => {
      t = setTimeout(() => {
        if (i < steps.length) { i += 1; setN(i); next(); } else { t = setTimeout(() => { i = 1; setN(1); next(); }, 2800); }
      }, steps[i - 1]?.d ?? 1500);
    };
    next();
    return () => clearTimeout(t);
  }, [animate, key, steps.length]); // eslint-disable-line react-hooks/exhaustive-deps
  return n;
};

// Hero phone: an in-app style replica that types a plate, finds it and opens the chat.
export const AppPhone = ({ plate = "AB 123 CD", className = "", testid, captionClass = "text-muted-foreground" }) => {
  const { lang, t } = useT();
  const d = D[lang] || D.en;
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.3 });
  const reduce = useReducedMotion();
  const steps = build("search", d);
  const animate = inView && !reduce;
  const n = useSteps(steps, animate, lang);
  const shown = steps.slice(0, n);
  const msgs = shown.filter((s) => s.kind === "msg");
  const chat = msgs.length > 0;
  const typing = shown.length === 1;
  return (
    <figure ref={ref} className={className} data-testid={testid}>
      <div className="overflow-hidden rounded-[2rem] border border-[#2a2d66] bg-[#0d0f2b] p-1.5 dark:border-white/20 shadow-[0_40px_80px_-40px_rgba(23,25,64,0.7)]">
        <div className="relative aspect-[521/1024] overflow-hidden rounded-[1.6rem] bg-[#171940] text-white">
          <AnimatePresence initial={false}>
            {!chat ? (
              <motion.div key="home" className="absolute inset-0 flex flex-col px-4 pt-4" initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.4 }}>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1.5"><Mark className="h-5 w-5" /><span className="font-display text-sm font-bold tracking-tight">chatplate</span></span>
                  <Menu className="h-5 w-5 text-white/80" aria-hidden />
                </div>
                <p className="mt-6 font-display text-[17px] font-bold leading-tight">{d.homeTitle}</p>
                <div className="mt-4 flex items-center justify-between rounded-xl border border-[#2E3CFD]/70 bg-[#0d1543] px-3 py-2.5 text-[13px]">
                  {shown.length ? <Typing text={plate} run={animate && typing} /> : <span className="text-white/50">{d.typePh}</span>}
                  <Search className={`${ICON} text-[#F5C842]`} aria-hidden />
                </div>
                <p className="mt-2 text-[11px] text-white/70">{d.hint}</p>
                {shown.some((s) => s.kind === "result") && (
                  <motion.div initial={reduce ? false : { opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-4"><Step s={{ kind: "result" }} d={d} plate={plate} /></motion.div>
                )}
                <div className="mt-auto -mx-4 flex justify-around border-t border-white/10 bg-[#141638] py-3 text-white/60">
                  <HomeIcon className={`${ICON} text-[#5d68ff]`} aria-hidden /><Send className={ICON} aria-hidden /><UserCircle className={ICON} aria-hidden />
                </div>
              </motion.div>
            ) : (
              <motion.div key="chat" className="absolute inset-0 flex flex-col" initial={reduce ? false : { opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}>
                <div className="flex items-center gap-2 border-b border-white/10 px-3 py-3">
                  <ChevronLeft className="h-5 w-5 text-white/80" aria-hidden />
                  <Plate text={plate} size="sm" />
                  <span className="ml-auto flex gap-3 text-white/80"><Phone className={ICON} aria-label={d.audioCall} /><Video className={ICON} aria-label={d.videoCall} /></span>
                </div>
                <div className="flex flex-1 flex-col justify-end gap-2 p-3">
                  {msgs.map((s, i) => (
                    <motion.div key={i} initial={reduce ? false : { opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}><Bubble s={s} d={d} /></motion.div>
                  ))}
                </div>
                <div className="flex items-center gap-2 px-3 pb-4">
                  <span className="flex-1 rounded-full border border-white/30 px-3 py-2 text-[12px] text-white/50">{d.typeMsg}</span>
                  <Mic className={`${ICON} text-white/80`} aria-hidden />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      <figcaption className={`mt-2 text-center text-[11px] uppercase tracking-wider ${captionClass}`}>{t.common.illustration}</figcaption>
    </figure>
  );
};

// Animated product demo. Plays while in view, pauses outside, static with reduced motion.
export const Demo = ({ scenario = "search", plate = "AB 123 CD", className = "", testid, captionClass = "text-muted-foreground" }) => {
  const { lang } = useT();
  const d = D[lang] || D.en;
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.3 });
  const reduce = useReducedMotion();
  const steps = build(scenario, d);
  const animate = inView && !reduce;
  const n = useSteps(steps, animate, `${scenario}-${lang}`);

  const isCall = scenario === "audio" || scenario === "video";
  const scene = steps.find((s) => s.kind === "scene");
  const done = steps.slice(0, n).some((s) => s.kind === "done");
  const shown = steps.slice(0, n).filter((s) => !["scene", "done"].includes(s.kind));
  return (
    <figure ref={ref} className={`relative ${className}`} data-testid={testid}>
      <div className="overflow-hidden rounded-[1.75rem] border border-[#2a2d66] bg-[#171940] text-white shadow-[0_40px_80px_-40px_rgba(23,25,64,0.6)] dark:border-white/20">
        {isCall ? (
          <div className="h-[380px]">{scenario === "audio" ? <Call d={d} run={animate} plate={plate} /> : <VideoCall d={d} run={animate} />}</div>
        ) : (
          <>
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
              <Plate text={plate} size="sm" />
              <span className="flex gap-3 text-white/80"><Phone className={ICON} aria-label={d.audioCall} /><Video className={ICON} aria-label={d.videoCall} /><Mic className={ICON} aria-label={d.voice} /></span>
            </div>
            {scene && <div className="px-3 pt-3"><Scene moved={done} d={d} /></div>}
            <div className="flex min-h-[260px] flex-col justify-end gap-2 p-3">
              <AnimatePresence initial={false}>
                {shown.map((s, i) => (
                  <motion.div key={`${scenario}-${i}`} initial={reduce ? false : { opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}>
                    <Step s={s} d={d} run={animate} plate={plate} isLast={i === shown.length - 1} />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </>
        )}
      </div>
      <figcaption className={`mt-2 text-center text-[11px] uppercase tracking-wider ${captionClass}`}>{useT().t.common.illustration}</figcaption>
    </figure>
  );
};

export const CheckList = ({ items = [], className = "" }) => (
  <ul className={`space-y-2.5 ${className}`}>
    {items.map((x, i) => <li key={i} className="flex gap-3"><Check className="mt-1 h-4 w-4 shrink-0 stroke-[2.25] text-primary" aria-hidden /><span>{x}</span></li>)}
  </ul>
);
